import { useTranslation } from "react-i18next";
import React, { useEffect } from "react";
import { Container, Row, Col, Card, Spinner } from "reactstrap";
import { Link, RouteComponentProps, useHistory } from "react-router-dom";
import xlsx from "json-as-xlsx";

import { BreadCrumb, BreadCrumbSelect } from "../../components/Common";
import { ToastContainer } from "react-toastify";
import { NoPurchasesStudentsReportDto } from "../../interfaces/reports";
import { useDispatch, useSelector } from "react-redux";
import { State } from "../../interfaces";
import {
  getNoPurchasesParentsReport,
  getNoPurchasesStudentsReport,
  resetNoPurchasesParentsReport,
  resetNoPurchasesStudentsReport,
} from "../../store/actions";

type TProps = RouteComponentProps & {};
export const ReportsPage: React.FC<TProps> = () => {
  //#region hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  //#endregion

  const {
    loading: loadingStudentWithNoPurchasesReport,
    response: responseStudentWithNoPurchasesReport,
  } = useSelector(
    (state: any): State<NoPurchasesStudentsReportDto[]> =>
      state.report.getNoPurchasesStudentsReport
  );

  const {
    loading: loadingParentsWithNoPurchasesReport,
    response: responseParentsWithNoPurchasesReport,
  } = useSelector(
    (state: any): State<NoPurchasesStudentsReportDto[]> =>
      state.report.getNoPurchasesParentsReport
  );

  document.title = `${t("documentTitle", {
    pageName: "reports",
  })}`;

  const downloadStudentsWithNoPurchasesReport = () => {
    dispatch(getNoPurchasesStudentsReport(history));
  };

  const resetNoPurchasesSReport = () => {
    dispatch(resetNoPurchasesStudentsReport());
  };

  useEffect(() => {
    if (
      !loadingStudentWithNoPurchasesReport &&
      responseStudentWithNoPurchasesReport
    ) {
      const now = new Date();
      let settings = {
        fileName: `No Purchases Students ${now.getDate()}-${
          now.getMonth() + 1
        }-${now.getFullYear()}`,
        extraLength: 3,
        writeMode: "writeFile",
        writeOptions: {},
      };
      let data = [
        {
          sheet: "sheet 1",
          columns: [
            { label: "Id", value: "id" },
            { label: "Name", value: "name" },
            { label: "Gender", value: "gender" },
            { label: "Phone Number", value: "phoneNumber" },
            { label: "Course Name", value: "courseName" },
            { label: "Grade Name", value: "gradeName" },
            {
              label: "Created Date",
              value: "createdDate",
              format: "dd-mm-yy hh:mm",
            },
          ],
          content: responseStudentWithNoPurchasesReport.map((item) => ({
            id: item.id,
            name: item.name,
            gender: item.gender,
            phoneNumber: item.phoneNumber,
            courseName: item.courseName,
            gradeName: item.gradeName,
            createdDate: item.createdDate,
          })),
        },
      ];
      xlsx(data, settings, resetNoPurchasesSReport);
    }
  }, [loadingStudentWithNoPurchasesReport]);

  const downloadParentsWithNoPurchasesReport = () => {
    dispatch(getNoPurchasesParentsReport(history));
  };

  const resetNoPurchasesPReport = () => {
    dispatch(resetNoPurchasesParentsReport());
  };

  useEffect(() => {
    if (
      !loadingParentsWithNoPurchasesReport &&
      responseParentsWithNoPurchasesReport
    ) {
      const now = new Date();
      let settings = {
        fileName: `No Purchases Parents ${now.getDate()}-${
          now.getMonth() + 1
        }-${now.getFullYear()}`,
        extraLength: 3,
        writeMode: "writeFile",
        writeOptions: {},
      };
      let data = [
        {
          sheet: "sheet 1",
          columns: [
            { label: "Id", value: "id" },
            { label: "Name", value: "name" },
            { label: "Gender", value: "gender" },
            { label: "Phone Number", value: "phoneNumber" },
            {
              label: "Created Date",
              value: "createdDate",
              format: "dd-mm-yy hh:mm",
            },
          ],
          content: responseParentsWithNoPurchasesReport.map((item) => ({
            id: item.id,
            name: item.name,
            gender: item.gender,
            phoneNumber: item.phoneNumber,
            createdDate: item.createdDate,
          })),
        },
      ];
      xlsx(data, settings, resetNoPurchasesPReport);
    }
  }, [loadingParentsWithNoPurchasesReport]);

  return (
    <React.Fragment>
      <ToastContainer newestOnTop={true} />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("reports")}
            pageTitle={t("reports")}
            component={
              <BreadCrumbSelect
                options={[
                  {
                    title: "reports",
                    url: "/reports",
                    selected: true,
                  },
                  {
                    title: "report2",
                    url: "/reports-v2",
                    selected: false,
                  },
                ]}
              />
            }
          />
          <Row>
            <Col className="col-xl-5 col-lg-3 col-md-2" key={1}>
              <Card>
                <div className="card-body text-center">
                  <h4 className="mt-2 mb-1">{t("studentsWithNoPurchases")}</h4>
                  <p className="text-muted mb-2">{t("forCurrentSemester")}</p>
                  <p className="text-muted">
                    {t("studentsWithNoPurchasesDescription")}
                  </p>
                  <br />
                  <button
                    className={"btn btn-success w-100"}
                    onClick={downloadStudentsWithNoPurchasesReport}
                  >
                    {loadingStudentWithNoPurchasesReport ? (
                      <Spinner
                        size="sm"
                        className="flex-shrink-0"
                        role="status"
                      ></Spinner>
                    ) : (
                      t("download")
                    )}
                  </button>
                </div>
              </Card>
            </Col>
            <Col key={2} className="col-xl-5 col-lg-3 col-md-2 ">
              <Card>
                <div className="card-body text-center">
                  <h4 className="mt-2 mb-1">{t("parentWithNoPurchases")}</h4>
                  <p className="text-muted mb-2">{t("forCurrentSemester")}</p>
                  <p className="text-muted">
                    {t("parentWithNoPurchasesDescription")}
                  </p>
                  <br />
                  <button
                    className={"btn btn-success w-100"}
                    onClick={downloadParentsWithNoPurchasesReport}
                  >
                    {loadingParentsWithNoPurchasesReport ? (
                      <Spinner
                        size="sm"
                        className="flex-shrink-0"
                        role="status"
                      ></Spinner>
                    ) : (
                      t("download")
                    )}
                  </button>
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};
